import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { detectReferencePoint } from '../../../functions/launcherElementFunctions/detectReferencePoint';
import { getElementById } from '../../../functions/launcherElementFunctions/getElementById';
import { updateObjectNestedProperties } from '../../../functions/objectFunctions/updateObjectNestedProperties';
import { extractNumber } from '../../../functions/utils/extractNumber';
import { extractUnit } from '../../../functions/utils/extractUnit';
import { useElements, useScene } from '../../../hooks';
import {
  useCursor,
  useHandle,
  useKeyboard,
  useMouse,
} from '../../../hooks/useEditor';
import {
  CursorData,
  HandleData,
  KeyboardData,
  MouseData,
} from '../../../types/hooks';
import { LauncherElement } from '../../../types/launcherElement';
import { sizeUnits } from '../../../types/units';
import { Bottom } from './handleComponents/Bottom';
import { BottomLeft } from './handleComponents/BottomLeft';
import { BottomRight } from './handleComponents/BottomRight';
import { Dimensions } from './handleComponents/Dimensions';
import { Left } from './handleComponents/Left';
import { Right } from './handleComponents/Right';
import { Top } from './handleComponents/Top';
import { TopLeft } from './handleComponents/TopLeft';
import { TopRight } from './handleComponents/TopRight';

export const Handle = () => {
  const scene = useScene();
  const [mouse] = useMouse<MouseData>();
  const [keyboard] = useKeyboard<KeyboardData>();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });
  const [elements] = useElements();
  const [handle, setHandle] = useHandle<HandleData>();
  const [, setCursor] = useCursor<CursorData>();

  const [isDraggingX, setDraggingX] = useState(false);
  const [isDraggingY, setDraggingY] = useState(false);
  const [isResizingX, setResizingX] = useState(false);
  const [isResizingY, setResizingY] = useState(false);
  const [isResizingReversedX, setResizingReversedX] = useState(false);
  const [isResizingReversedY, setResizingReversedY] = useState(false);

  const isDragging = isDraggingX || isDraggingY;
  const isResizing = isResizingX || isResizingY;

  const point = detectReferencePoint(selectedElement);

  let xPropName: 'left' | 'right' = 'left';
  let yPropName: 'top' | 'bottom' = 'top';
  switch (point) {
    case 'topLeft':
      xPropName = 'left';
      yPropName = 'top';
      break;
    case 'topRight':
      xPropName = 'right';
      yPropName = 'top';
      break;
    case 'bottomLeft':
      xPropName = 'left';
      yPropName = 'bottom';
      break;
    case 'bottomRight':
      xPropName = 'right';
      yPropName = 'bottom';
      break;
  }

  // Show Handle above selected Element and update when Element changes
  useEffect(() => {
    if (scene.selectedElementIDs.length === 1) {
      const launcherWindowDiv = document.getElementById('launcherWindow');
      const launcherWindowRect = launcherWindowDiv?.getBoundingClientRect()!;

      const elementDiv = document.getElementById(lastSelectedElementID);
      const elementRect = elementDiv?.getBoundingClientRect()!;
      if (!elementRect) return;

      const elementDim = {
        x: elementRect.left && elementRect.left - launcherWindowRect.left,
        y: elementRect.top && elementRect.top - launcherWindowRect.top,
        width: elementRect.width,
        height: elementRect.height,
      };

      const isElementRectOfZeroSize =
        !isResizing && !elementRect.width && !elementRect.height;

      setHandle({
        isVisible: isElementRectOfZeroSize ? false : true,
        x: elementDim?.x + scene.offset.x,
        y: elementDim?.y + scene.offset.y,
        width: elementDim?.width,
        height: elementDim?.height,
      });
    } else if (scene.selectedElementIDs.length > 1) {
      // TODO: Multiselect
    } else {
      setHandle({
        isVisible: false,
      });
    }
  }, [selectedElement]);

  // Apply transformations to selected Element
  useEffect(() => {
    if (!(isDragging || isResizing)) return;
    if (keyboard.keysDown?.includes('Space')) return;

    // Hide cursor while dragging
    isDragging && setCursor({ isVisible: false });

    let [element, parent] = getElementById(elements, lastSelectedElementID);
    if (!element) return;

    const newDim = {
      x: element?.props?.class?.[xPropName],
      y: element?.props?.class?.[yPropName],
      width: element?.props?.class?.width,
      height: element?.props?.class?.height,
    };

    // Set position: absolute when interacting with handle
    if (isDragging) {
      element = updateObjectNestedProperties(element, {
        'props.class.position': 'absolute',
      });
    }

    // Set value for undefined dimensions when interacting with handle
    const parentDiv = document.getElementById(parent?.id || 'launcherWindow');
    const parentRect = parentDiv?.getBoundingClientRect()!;

    const elementDiv = document.getElementById(lastSelectedElementID);
    const elementRect = elementDiv?.getBoundingClientRect()!;

    if (isDragging && (isNaN(newDim.x) || isNaN(newDim.y))) {
      newDim.x =
        Math.abs(elementRect?.[xPropName]) - Math.abs(parentRect?.[xPropName]);
      newDim.y =
        Math.abs(elementRect?.[yPropName]) - Math.abs(parentRect?.[yPropName]);
    }

    if (isResizing && (isNaN(newDim.width) || isNaN(newDim.height))) {
      newDim.width = elementRect.width;
      newDim.height = elementRect.height;
    }

    // Calculate new position and size based on diffs
    const posDiff = mouse.movement;
    const sizeDiff = {
      width: (isResizingReversedX ? -1 : 1) * posDiff.x,
      height: (isResizingReversedY ? -1 : 1) * posDiff.y,
    };

    if (isDraggingX) newDim.x += posDiff.x * (xPropName === 'right' ? -1 : 1);
    if (isDraggingY) newDim.y += posDiff.y * (yPropName === 'bottom' ? -1 : 1);

    if (isResizingX) newDim.width += sizeDiff.width;
    if (isResizingY) newDim.height += sizeDiff.height;

    // Apply new dimensions to Element
    if (isDragging) {
      element = updateObjectNestedProperties(element, {
        [`props.class.${xPropName}`]: newDim.x,
        [`props.class.${yPropName}`]: newDim.y,
      });
    }
    if (isResizing) {
      element = updateObjectNestedProperties(element, {
        [`props.class.${xPropName}`]: newDim.x,
        [`props.class.${yPropName}`]: newDim.y,
        'props.class.width': `${newDim.width}px`,
        'props.class.height': `${newDim.height}px`,
      });
    }

    if (element) setSelectedElement(element);
  }, [mouse.pos]);

  useEffect(() => {
    if (mouse.isDown) {
      // Instant dragging
      // (only over elements with specified 'element-type' attr)
      // ! it breakes the dragging animation, becouse of the notransition prop
      if (mouse.hoveredElementType) {
        setDraggingX(true);
        setDraggingY(true);
      }
    } else {
      // Reset on mouseUp
      setDraggingX(false);
      setDraggingY(false);
      setResizingX(false);
      setResizingY(false);
      setResizingReversedX(false);
      setResizingReversedY(false);
    }
  }, [mouse.isDown]);

  const getValueForDimension = (dimension: 'width' | 'height') => {
    const value = selectedElement?.props?.class?.[dimension];
    const unit = extractUnit(value, sizeUnits);
    const number = extractNumber(value);

    return number !== null ? (unit === 'px' ? number : value) : 'auto';
  };

  const drawDimensions = () => {
    const width = getValueForDimension('width');
    const height = getValueForDimension('height');

    if (width === 'auto' && height === 'auto') return;
    return <Dimensions>{`${width} x ${height}`}</Dimensions>;
  };

  return (
    <Container
      $notransition={isDragging || isResizing}
      $hidden={!handle.isVisible || (isDragging && !isResizing)}
      $disabled={handle.isDisabled}
      style={{
        position: 'absolute',
        left: handle.x,
        top: handle.y,
        width: handle.width,
        height: handle.height,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        if (e.target !== e.currentTarget) return;
        setDraggingX(true);
        setDraggingY(true);
      }}
    >
      <Top
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingY(true);
          setResizingReversedY(true);
          if (yPropName !== 'bottom') setDraggingY(true);
        }}
      />
      <Right
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          if (xPropName === 'right') setDraggingX(true);
        }}
      />
      <Bottom
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingY(true);
          if (yPropName === 'bottom') setDraggingY(true);
        }}
      />
      <Left
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          setResizingReversedX(true);
          if (xPropName !== 'right') setDraggingX(true);
        }}
      />

      <TopLeft
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          setResizingY(true);
          setResizingReversedX(true);
          setResizingReversedY(true);
          if (xPropName !== 'right') setDraggingX(true);
          if (yPropName !== 'bottom') setDraggingY(true);
        }}
      />
      <TopRight
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          setResizingY(true);
          setResizingReversedY(true);
          if (xPropName === 'right') setDraggingX(true);
          if (yPropName !== 'bottom') setDraggingY(true);
        }}
      />
      <BottomLeft
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          setResizingY(true);
          setResizingReversedX(true);
          if (xPropName !== 'right') setDraggingX(true);
          if (yPropName === 'bottom') setDraggingY(true);
        }}
      />
      <BottomRight
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          setResizingX(true);
          setResizingY(true);
          if (xPropName === 'right') setDraggingX(true);
          if (yPropName === 'bottom') setDraggingY(true);
        }}
      />

      {drawDimensions()}
    </Container>
  );
};

const Container = styled.div<{
  $notransition?: boolean;
  $hidden?: boolean;
  $disabled?: boolean;
}>`
  outline: 1px dashed #333;
  box-shadow: 0px 0px 1px 1px #eee;

  display: flex;
  justify-content: center;

  opacity: 1;
  transition:
    opacity 0.3s ease,
    left 0.5s cubic-bezier(0.075, 0.82, 0.165, 1),
    top 0.5s cubic-bezier(0.075, 0.82, 0.165, 1),
    width 0.5s cubic-bezier(0.075, 0.82, 0.165, 1),
    height 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);

  ${({ $hidden }) =>
    $hidden &&
    `
    opacity: 0;
    pointer-events: none;
  `}

  ${({ $notransition }) =>
    $notransition &&
    `
    transition: none;
  `}

  ${({ $disabled }) =>
    $disabled &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}

  & > * {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  &:hover > * {
    opacity: 1;
  }
`;
